import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getThumbnailNew } from 'services/format/thumbnails';
import GaEventManager from 'services/ga/gaEventManager';
import { getAttachmentHandler, getSortedCertificates } from 'state/helpers';

const fetchAuctionAttachments = createAsyncThunk(
  'auctions/fetchAuctionAttachments',
  async (payload: { auction: AuctionPreviewTypes.Auction }, { getState, dispatch, signal }) => {
    const { auction } = payload;
    const state = getState() as RootState;
    let rawAttachments = { ...state.auctions?.rawAttachments } || {};

    if (!auction?.auction_number || !auction?.auction_id) {
      return { attachments: null, rawAttachments };
    }

    const auctionNumber = Number(auction.auction_number);
    let attachments: AuctionsTypes.CachedAttachment = {
      certificates: [],
      images: [],
      video360: [],
      auctionNumber,
    };

    const sessionKey = 'attachments';
    const sessionData = sessionStorage.getItem(sessionKey);

    const cachedData = sessionData ? JSON.parse(sessionData) : {};

    const handlerOnGet = getAttachmentHandler(sessionKey, auctionNumber);

    const cachedAttachments = await new Proxy(cachedData, handlerOnGet);

    if (rawAttachments?.[auctionNumber]) {
      return {
        attachments: rawAttachments?.[auctionNumber],
        rawAttachments,
      };
    }

    if (auctionNumber in cachedData) {
      attachments = cachedAttachments[auctionNumber];
    } else {
      const videoUrl = process.env.REACT_APP_AWS_VIDEO_URL;

      const { data }: { data: { images: string[]; certificates: []; video_360: boolean } } =
        await axios.get(`/api/buyers/${auction.auction_number}/auction_images?allMedia=true`, {
          signal,
        });

      attachments.images = data?.images
        ? data.images
            .filter((image) => image.trim() !== '')
            .map((image) => ({
              title: auction.description,
              url: image,
              thumbnail: image,
            }))
        : [];

      attachments.video360 = data?.video_360
        ? [
            {
              title: auction.description,
              url: videoUrl?.replace('{auctionId}', auction.auction_number) ?? '',
              thumbnail: getThumbnailNew('360'),
            },
          ]
        : [];

      const hasCertificates = Object.values(data?.certificates).length > 0;

      if (hasCertificates) {
        const sortedCertificates = getSortedCertificates(data?.certificates);

        attachments.certificates = sortedCertificates.map((document) => {
          const thumbnail =
            document.type && document.type.toLowerCase().search('gia') > -1 ? 'gia' : undefined;

          return {
            title: 'Certificates',
            url: document.url,
            thumbnail: getThumbnailNew(thumbnail),
          };
        });
      }

      if (
        !hasCertificates &&
        auction.auctioned_subsets?.length === 1 &&
        auction.auctioned_subsets[0].data?.diamond_certificate?.val === 'GIA' &&
        auction.auctioned_subsets[0].data?.diamond_certificate_number?.val
      ) {
        attachments.certificates = [
          {
            title: 'Certificates',
            url: `https://www.gia.edu/report-check?reportno=${auction.auctioned_subsets[0].data?.diamond_certificate_number.val}`,
            thumbnail: getThumbnailNew('gia'),
          },
        ];
      }

      cachedAttachments[auctionNumber] = attachments;
    }

    rawAttachments[auctionNumber] = attachments;

    return {
      attachments,
      rawAttachments,
    };
  },
);

const initialState: AttachmentsTypes.State = {
  attachment: {
    isShow: false,
    type: undefined,
    attachments: null,
    selectedAttachment: undefined,
  },
  rawAttachments: null,
  attachments: {},
};

export const attachmentsSlice = createSlice({
  name: 'attachments',
  initialState,
  reducers: {
    showAttachmentDialog: (
      state,
      action: PayloadAction<{
        attachmentType: AuctionsTypes.AttachmentTypes;
        selectedAttachment?: number;
        eventOrigin?: string;
        auctionNumber: string;
      }>,
    ) => {
      const { attachmentType, auctionNumber, selectedAttachment, eventOrigin } = action.payload;
      const {
        attachment: { attachments },
      } = state;

      if (!attachments || !attachmentType || !attachments[attachmentType]) {
        return {
          ...state,
          attachment: {
            ...state.attachment,
            isShow: false,
            type: attachmentType,
            selectedAttachment,
          },
        };
      }

      GaEventManager.GA4SendActionOpen({
        location4: eventOrigin,
        actionedObject: attachmentType,
        fieldName: 'auctionId',
        fieldValue: auctionNumber,
      });

      return {
        ...state,
        attachment: {
          ...state.attachment,
          isShow: attachments[attachmentType].length > 0,
          type: attachmentType,
          selectedAttachment,
        },
      };
    },
    closeAttachmentDialog: (state) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          type: undefined,
          isShow: false,
          selectedAttachment: undefined,
        },
      };
    },
    setAttachmentsData: (state, action: PayloadAction<AuctionsTypes.BuyersServiceAuction[]>) => {
      const attachments: Record<number, AttachmentsTypes.Attachment> = action.payload.reduce(
        (all, data) => {
          const prevData = state.attachments[data.auctionId];
          return {
            ...all,
            [data.auctionId]: {
              ...prevData,
              mainImage: data.mainImage,
            },
          };
        },
        {},
      );

      return {
        ...state,
        attachments,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuctionAttachments.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      return {
        ...state,
        attachment: {
          ...state.attachment,
          attachments: action.payload.attachments,
        },
        rawAttachments: action.payload.rawAttachments,
      };
    });
    builder.addCase(fetchAuctionAttachments.rejected, (state, action) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          attachments: null,
        },
      };
    });
  },
});

export const attachmentsActions = {
  ...attachmentsSlice.actions,
  fetchAuctionAttachments,
};

export default attachmentsSlice.reducer;
